'use client';

import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { FidIcon, RichText, useClientData } from '@headless-workspace/domain/common/client';
import {
    GlowRating,
    GlowTextBody,
    GlowTextBodySmall,
    PropsWithClassName,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { DateHelpers } from '@headless-workspace/utils';
import { ProductReview } from '../../../../server';
import { ReviewPurchaserType } from '../../../../server/lib/productDetail';
import { ReviewPurchaserBadge, ReviewReportingContainer, ReviewTitleAndContent } from './components';

type ReviewPostListItemProps = PropsWithLocale &
    PropsWithClassName &
    Omit<ProductReview, 'vote' | 'purchaserType'> & {
        truncateContent?: boolean;
        purchaserType?: ReviewPurchaserType;
        vote?: { like: number; dislike: number };
        TagName: keyof HTMLElementTagNameMap;
    };

export const ReviewCard: Component<ReviewPostListItemProps> = ({
    id: reviewId,
    rating,
    createdAt,
    userName,
    userGender,
    userAge,
    userCard,
    purchaserType,
    purchasedProductSince,
    title,
    content,
    isRecommended,
    source,
    vote,
    translatable,
    locale,
    className,
    TagName,
    truncateContent = true,
}) => {
    const t = useTranslations('Discover.Pdp.ratingAndReviews');

    // Retrieve MY_SEPHORA activation flag
    const { getClientFlags } = useClientData();
    const isMySephoraActivated = getClientFlags('isMySephoraActivated');

    return (
        <TagName
            className={clsx(
                'flex flex-col',
                'py-1 gap-1',
                'border-solid border-medium border-border-primary rounded-0.5',
                className,
            )}
        >
            <div className={'flex justify-between px-1'}>
                <GlowRating rating={rating} className={'gap-0.5'} />
                <GlowTextBodySmall text={DateHelpers.formatDateToShortDateString(locale, createdAt)} />
            </div>
            <div className={'flex justify-between px-1'}>
                <div className={'flex flex-col'}>
                    <GlowTextBody text={userName} fontWeight={'bold'} />
                    {userGender && <GlowTextBody text={userGender} />}
                    {userAge && <GlowTextBody text={userAge} />}
                </div>
                {!isMySephoraActivated && <FidIcon userCard={userCard} isMySephoraActivated={isMySephoraActivated} />}
            </div>

            {(purchaserType || purchasedProductSince) && (
                <div className={'flex flex-col px-1'}>
                    {purchaserType && <ReviewPurchaserBadge purchaserType={purchaserType} />}
                    {purchasedProductSince && (
                        <GlowTextBodySmall
                            text={t('label.purchaseProductSince', {
                                time: purchasedProductSince,
                            })}
                            color={'tertiary'}
                        />
                    )}
                </div>
            )}

            <ReviewTitleAndContent
                title={title}
                content={content}
                translatable={translatable}
                truncateContent={truncateContent}
                locale={locale}
            />
            {isRecommended !== undefined && (
                <GlowTextBody text={t('label.recommends', { isRecommended })} fontWeight={'bold'} className={'px-1'} />
            )}
            {source && (
                <div className={'flex justify-center rounded-0.5 bg-surface-secondary p-0.5 mx-1'}>
                    <RichText
                        fontSize={'bodySmall'}
                        color={'primary'}
                        messageKey={'Discover.Pdp.ratingAndReviews.action.source'}
                        values={{
                            label: source.label,
                        }}
                        links={{
                            brandLink: {
                                href: source.href,
                            },
                        }}
                        dataTestId={'review-source'}
                    />
                </div>
            )}
            {vote && <ReviewReportingContainer reviewId={reviewId} vote={vote} />}
        </TagName>
    );
};
